import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Plus,
  Building2,
  MapPin,
  Phone,
  Pencil,
  ChevronRight,
  MoreVertical,
  SlidersHorizontal,
  X,
  CheckCircle2,
  XCircle,
  Mail,
  User as UserIcon,
  Handshake,
  Link2,
  Copy,
  Hash,
  Navigation,
  MessageCircle,
  PhoneCall,
  Image as ImageIcon,
  Sparkles,
  FileText,
} from "lucide-react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

// School avatar palette — same colors as /orcamentos cards for consistency
const SCHOOL_PALETTE = [
  { bg: "bg-violet-100", icon: "text-violet-600" },
  { bg: "bg-emerald-100", icon: "text-emerald-600" },
  { bg: "bg-amber-100", icon: "text-amber-600" },
  { bg: "bg-rose-100", icon: "text-rose-600" },
  { bg: "bg-sky-100", icon: "text-sky-600" },
  { bg: "bg-fuchsia-100", icon: "text-fuchsia-600" },
  { bg: "bg-indigo-100", icon: "text-indigo-600" },
  { bg: "bg-teal-100", icon: "text-teal-600" },
];

function schoolColor(name = "") {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return SCHOOL_PALETTE[h % SCHOOL_PALETTE.length];
}

function cityLine(s) {
  const parts = [s.neighborhood, s.city, s.state].filter(Boolean);
  return parts.join(" - ");
}

function DetailRow({ icon: Icon, label, value, testid, action }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 py-1.5" data-testid={testid}>
      <Icon className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold leading-none">{label}</p>
        <p className="text-[12px] text-slate-800 mt-0.5 break-words">{value}</p>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

// Normalize WhatsApp number: keep digits, prefix 55 if missing, remove leading zeros from DDD
function waUrl(raw) {
  if (!raw) return "";
  let d = String(raw).replace(/\D/g, "");
  if (!d) return "";
  if (!d.startsWith("55") && d.length <= 11) d = "55" + d;
  return `https://wa.me/${d}`;
}

function telUrl(raw) {
  if (!raw) return "";
  const d = String(raw).replace(/\D/g, "");
  return d ? `tel:${d}` : "";
}

function mapsUrl(school) {
  const street = [school.address, school.address_number].filter(Boolean).join(", ");
  const parts = [
    street,
    school.neighborhood,
    school.city,
    school.state ? `${school.state} Brasil` : "Brasil",
  ].filter(Boolean);
  if (!parts.length) return "";
  const q = encodeURIComponent(parts.join(", "));
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
}

function ActionBtn({ href, label, icon: Icon, testid, color = "violet" }) {
  if (!href) return null;
  const palette = {
    violet: "bg-violet-50 text-[#7c3aed] border-violet-200 hover:bg-violet-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100",
    sky: "bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100",
  };
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      data-testid={testid}
      className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full border px-2.5 py-1 ${palette[color]}`}
    >
      <Icon className="w-3 h-3" /> {label}
    </a>
  );
}

function SchoolCard({ s, expandedId, onToggle, onPhotoFetched }) {
  const color = schoolColor(s.name);
  const open = expandedId === s.id;
  // Se o diretor já se cadastrou, não exibir mais o link de convite — a escola
  // está "ativada" e o convite não deve mais ser compartilhado por engano.
  const alreadyRegistered = !!(s.has_users || s.registered_at);
  const inviteUrl = s.invite_token && !alreadyRegistered
    ? `${window.location.origin}/convite/${s.invite_token}`
    : "";
  const [fetchingPhoto, setFetchingPhoto] = useState(false);
  const photoUrl = s.photo_path
    ? `${API}/files/${s.photo_path}`
    : "";

  const copyInvite = (e) => {
    e.stopPropagation();
    if (!inviteUrl) return;
    try {
      navigator.clipboard.writeText(inviteUrl);
      toast.success("Link de convite copiado!");
    } catch (_) {
      toast.error("Não foi possível copiar");
    }
  };

  const fetchPhoto = async (e) => {
    e?.stopPropagation();
    setFetchingPhoto(true);
    try {
      await api.post(`/schools/${s.id}/fetch-google-photo`);
      toast.success("Foto encontrada no Google!");
      onPhotoFetched?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao buscar foto");
    } finally {
      setFetchingPhoto(false);
    }
  };

  return (
    <div
      data-testid={`school-card-${s.id}`}
      className="bg-white rounded-2xl border border-slate-100 card-shadow overflow-hidden"
    >
      <div className="p-3.5 flex items-start gap-3">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={s.name}
            className="w-12 h-12 rounded-xl object-cover shrink-0 border border-slate-200"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            data-testid={`school-photo-${s.id}`}
          />
        ) : (
          <div className={`w-12 h-12 rounded-full ${color.bg} flex items-center justify-center shrink-0`}>
            <Building2 className={`w-6 h-6 ${color.icon}`} strokeWidth={2.2} />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-heading text-sm font-black text-[#1e3a8a] uppercase tracking-tight leading-tight break-words">
                {s.name}
              </p>
              {(s.address || s.address_number) && (
                <p className="text-[11px] text-slate-700 mt-1 flex items-start gap-1">
                  <MapPin className="w-3 h-3 text-slate-400 shrink-0 mt-0.5" />
                  <span className="truncate">
                    {[s.address, s.address_number].filter(Boolean).join(", ")}
                  </span>
                </p>
              )}
              {cityLine(s) && (
                <p className="text-[11px] text-slate-500 pl-4 leading-tight">
                  {cityLine(s)}
                </p>
              )}

              <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                {s.active ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
                    <CheckCircle2 className="w-3 h-3" /> Ativa
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-rose-100 text-rose-700 border border-rose-200 px-2 py-0.5 rounded-full">
                    <XCircle className="w-3 h-3" /> Inativa
                  </span>
                )}
                {s.fdp_active && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-violet-100 text-violet-700 border border-violet-200 px-2 py-0.5 rounded-full">
                    <Handshake className="w-3 h-3" /> FDP
                  </span>
                )}
                {alreadyRegistered && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-semibold bg-sky-100 text-sky-700 border border-sky-200 px-2 py-0.5 rounded-full"
                    title={s.registered_user_email ? `Diretor: ${s.registered_user_email}` : "Diretor já cadastrado no sistema"}
                    data-testid={`chip-registered-${s.id}`}
                  >
                    <CheckCircle2 className="w-3 h-3" /> Diretor cadastrado
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col items-end gap-2 shrink-0">
              <button
                type="button"
                onClick={() => onToggle(s.id)}
                data-testid={`btn-expand-escola-${s.id}`}
                aria-label={open ? "Ocultar detalhes" : "Ver detalhes"}
                className={`w-8 h-8 rounded-full border flex items-center justify-center transition-all ${open ? "bg-violet-100 border-violet-200 text-[#7c3aed] rotate-90" : "bg-white border-slate-200 text-slate-500 hover:border-violet-200 hover:text-[#7c3aed]"}`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <Link to={`/escolas/${s.id}`} onClick={(e) => e.stopPropagation()}>
                <span
                  data-testid={`btn-editar-escola-${s.id}`}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#7c3aed] bg-violet-50 border border-violet-100 rounded-full px-2.5 py-1 hover:bg-violet-100"
                >
                  <Pencil className="w-3 h-3" /> Editar
                </span>
              </Link>
              {s.photo_path ? (
                <button
                  type="button"
                  onClick={fetchPhoto}
                  disabled={fetchingPhoto}
                  data-testid={`btn-fetch-photo-${s.id}`}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1 hover:bg-slate-100 disabled:opacity-50"
                  title="Baixar foto novamente do Google Places"
                >
                  <ImageIcon className="w-3 h-3" />
                  {fetchingPhoto ? "Buscando..." : "Trocar"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={fetchPhoto}
                  disabled={fetchingPhoto}
                  data-testid={`btn-fetch-photo-${s.id}`}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-1 hover:bg-emerald-100 disabled:opacity-50"
                  title="Buscar foto no Google Places"
                >
                  <ImageIcon className="w-3 h-3" />
                  {fetchingPhoto ? "Buscando..." : "Foto"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Rodapé do card: contagem de orçamentos.  Clicável vai para a lista
          filtrada pela escola — permite consulta rápida sem abrir detalhes. */}
      <Link
        to={`/orcamentos?school_id=${encodeURIComponent(s.id)}`}
        onClick={(e) => e.stopPropagation()}
        data-testid={`school-budget-count-${s.id}`}
        className="flex items-center justify-between px-3.5 py-2 border-t border-slate-100 bg-slate-50/60 hover:bg-slate-100/70 transition-colors"
      >
        <span className="text-[11px] font-semibold text-slate-600 inline-flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5 text-slate-400" strokeWidth={2.2} />
          {s.budget_count === 1
            ? "1 orçamento"
            : `${s.budget_count ?? 0} orçamentos`}
        </span>
        <span className="text-[10px] font-semibold text-[#7c3aed]">Ver</span>
      </Link>

      {/* Expanded details */}
      {open && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-3 space-y-1" data-testid={`school-details-${s.id}`}>
          {(s.address || s.address_number || s.neighborhood || s.city) && (
            <DetailRow
              icon={MapPin}
              label="Endereço"
              value={[
                [s.address, s.address_number].filter(Boolean).join(", "),
                cityLine(s),
              ].filter(Boolean).join(" — ")}
              testid={`det-address-${s.id}`}
              action={
                <ActionBtn
                  href={mapsUrl(s)}
                  label="Mapa"
                  icon={Navigation}
                  testid={`btn-mapa-${s.id}`}
                  color="sky"
                />
              }
            />
          )}
          <DetailRow icon={UserIcon} label="Diretor(a)" value={s.director_name} testid={`det-director-${s.id}`} />
          <DetailRow icon={Mail} label="E-mail do diretor" value={s.director_email} testid={`det-director-email-${s.id}`} />
          <DetailRow
            icon={Phone}
            label="Telefone"
            value={s.phone}
            testid={`det-phone-${s.id}`}
            action={
              <ActionBtn
                href={telUrl(s.phone)}
                label="Ligar"
                icon={PhoneCall}
                testid={`btn-ligar-${s.id}`}
                color="violet"
              />
            }
          />
          <DetailRow
            icon={MessageCircle}
            label="WhatsApp do diretor"
            value={s.whatsapp_director}
            testid={`det-wa-${s.id}`}
            action={
              <ActionBtn
                href={waUrl(s.whatsapp_director)}
                label="Abrir zap"
                icon={MessageCircle}
                testid={`btn-whatsapp-${s.id}`}
                color="emerald"
              />
            }
          />
          <DetailRow
            icon={Hash}
            label="CNPJ"
            value={s.cnpj}
            testid={`det-cnpj-${s.id}`}
            action={s.cnpj ? (
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(s.cnpj);
                    toast.success("CNPJ copiado");
                  } catch { toast.error("Erro ao copiar"); }
                }}
                data-testid={`btn-copy-cnpj-${s.id}`}
                className="inline-flex items-center gap-1 text-[11px] font-semibold bg-slate-900 text-white rounded-full px-2.5 py-1 hover:bg-slate-800"
              >
                <Copy className="w-3 h-3" /> Copiar
              </button>
            ) : null}
          />
          <DetailRow icon={Building2} label="Tipo" value={s.school_type ? s.school_type.charAt(0).toUpperCase() + s.school_type.slice(1) : ""} testid={`det-type-${s.id}`} />
          {s.fdp_active && (
            <DetailRow
              icon={Handshake}
              label="Faz Depois Paga"
              value={`Limite ${Number(s.fdp_max_value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} • ${s.fdp_period_months || 0} meses`}
              testid={`det-fdp-${s.id}`}
            />
          )}
          {inviteUrl && (
            <div className="pt-2 mt-2 border-t border-slate-200">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 flex items-center gap-1">
                <Link2 className="w-3 h-3" /> Link de convite para o diretor
              </p>
              <div className="flex items-center gap-2">
                <code className="text-[10px] bg-white border border-slate-200 rounded px-2 py-1 flex-1 truncate text-slate-700">
                  {inviteUrl}
                </code>
                <button
                  type="button"
                  onClick={copyInvite}
                  data-testid={`btn-copy-invite-${s.id}`}
                  className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-[#7c3aed] bg-white border border-violet-200 rounded-full px-2.5 py-1 hover:bg-violet-50"
                >
                  <Copy className="w-3 h-3" /> Copiar
                </button>
              </div>
            </div>
          )}

          <div className="pt-3 flex gap-2">
            <Link to={`/escolas/${s.id}`} className="flex-1">
              <Button className="w-full bg-[#7c3aed] hover:bg-[#6d28d9] text-white rounded-xl h-10" data-testid={`btn-abrir-ficha-${s.id}`}>
                <Pencil className="w-4 h-4 mr-2" />
                Editar ficha completa
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SchoolsList() {
  const PAGE_SIZE = 40;
  const [schools, setSchools] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | active | inactive | fdp | registered | pending
  const [showFilters, setShowFilters] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [batching, setBatching] = useState(false);
  const [total, setTotal] = useState(0);
  // Guards against stale responses when the user flips filters quickly:
  // the latest issued load wins, previous in-flight responses are discarded.
  const loadSeqRef = React.useRef(0);

  const buildParams = (nextOffset = 0) => {
    const params = { format: "paged", limit: PAGE_SIZE, offset: nextOffset };
    if (filter === "active") params.active = "true";
    else if (filter === "inactive") params.active = "false";
    else if (filter === "fdp") params.fdp_active = "true";
    else if (filter === "registered") params.has_users = "true";
    else if (filter === "pending") params.has_users = "false";
    if (q) params.q = q;
    return params;
  };

  const load = async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const r = await api.get("/schools", { params: buildParams(0) });
      if (seq !== loadSeqRef.current) return; // stale — a newer load is already in flight
      const data = r.data || {};
      setSchools(data.items || []);
      setTotal(data.total || 0);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  };

  const loadMore = async () => {
    const nextOffset = schools.length;
    const seq = loadSeqRef.current; // snapshot the current list's sequence
    setLoadingMore(true);
    try {
      const r = await api.get("/schools", { params: buildParams(nextOffset) });
      if (seq !== loadSeqRef.current) return; // filter changed meanwhile — drop
      const data = r.data || {};
      setSchools((prev) => [...prev, ...(data.items || [])]);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, showFilters]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const toggle = (id) => setExpandedId((cur) => (cur === id ? null : id));

  const totalLabel = useMemo(() => {
    const n = total;
    return `${n} ${n === 1 ? "escola" : "escolas"}`;
  }, [total]);

  return (
    <div className="space-y-4" data-testid="schools-page">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-4 flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
          <Building2 className="w-5 h-5 text-[#7c3aed]" strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl md:text-2xl font-black text-[#1e3a8a] leading-tight">
            Escolas cadastradas
          </h1>
          <p className="text-[12px] md:text-sm text-slate-500 leading-tight mt-0.5">
            Visualize, busque e gerencie todas as escolas.
          </p>
        </div>
        <Link to="/escolas/nova" className="shrink-0">
          <Button className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white rounded-xl h-10 px-3 md:px-4" data-testid="btn-nova-escola">
            <Plus className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Nova escola</span>
          </Button>
        </Link>
        <Button
          onClick={async () => {
            if (!window.confirm("Buscar fotos no Google para TODAS as escolas sem foto? Isso pode levar alguns segundos por escola.")) return;
            setBatching(true);
            try {
              const r = await api.post("/schools/fetch-all-google-photos");
              const d = r.data || {};
              toast.success(`Fotos: ${d.ok} ok • ${d.no_match} sem match • ${d.errors} erro(s)`);
              await load();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Falha na busca em lote");
            } finally {
              setBatching(false);
            }
          }}
          disabled={batching}
          variant="outline"
          className="h-10 border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-xl px-3 md:px-4 shrink-0"
          data-testid="btn-fetch-all-photos"
          title="Buscar foto no Google para todas as escolas sem foto"
        >
          <Sparkles className="w-4 h-4 md:mr-2" />
          <span className="hidden md:inline">{batching ? "Buscando..." : "Fotos (Google)"}</span>
        </Button>
      </div>

      {/* Search + filtros */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome, cidade ou bairro..."
            className="pl-9 h-11 rounded-xl bg-white"
            data-testid="input-search-escolas"
          />
        </div>
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          data-testid="btn-toggle-filtros-escolas"
          className={`shrink-0 inline-flex items-center gap-2 h-11 px-3 rounded-xl border text-[12px] font-semibold transition-colors ${showFilters ? "bg-[#7c3aed] text-white border-[#7c3aed]" : "bg-white text-[#7c3aed] border-violet-200 hover:bg-violet-50"}`}
        >
          {showFilters ? <X className="w-4 h-4" /> : <SlidersHorizontal className="w-4 h-4" />}
          Filtros
        </button>
      </div>

      {showFilters && (
        <div className="bg-white rounded-xl border border-slate-100 card-shadow p-3 flex gap-2 flex-wrap">
          {[
            { v: "all", l: "Todas" },
            { v: "active", l: "Ativas" },
            { v: "inactive", l: "Inativas" },
            { v: "fdp", l: "FDP" },
            { v: "registered", l: "Só cadastradas" },
            { v: "pending", l: "Sem cadastro" },
          ].map((f) => (
            <button
              key={f.v}
              type="button"
              onClick={() => setFilter(f.v)}
              data-testid={`filter-${f.v}`}
              className={`inline-flex items-center border rounded-xl px-3 py-2 text-[12px] font-semibold transition-colors ${filter === f.v ? "bg-[#7c3aed] text-white border-[#7c3aed]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}
            >
              {f.l}
            </button>
          ))}
        </div>
      )}

      {/* Counter */}
      {!loading && (
        <p className="text-[12px] text-slate-600" data-testid="schools-counter">
          Total de <span className="font-bold">{totalLabel}</span>
        </p>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-10 text-slate-500">Carregando...</div>
      ) : schools.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 card-shadow p-12 text-center">
          <Building2 className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 font-medium">Nenhuma escola encontrada</p>
          <p className="text-sm text-slate-500 mt-1">Clique em "Nova escola" para cadastrar.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {schools.map((s) => (
            <SchoolCard key={s.id} s={s} expandedId={expandedId} onToggle={toggle} onPhotoFetched={load} />
          ))}
          {/* Pagination footer */}
          <div className="flex flex-col items-center gap-2 pt-3">
            <p className="text-[11px] text-slate-500">
              Mostrando <strong>{schools.length}</strong> de <strong>{total}</strong>
            </p>
            {schools.length < total && (
              <Button
                type="button"
                variant="outline"
                onClick={loadMore}
                disabled={loadingMore}
                className="h-10 px-6 rounded-xl border-slate-200 text-slate-700 font-semibold"
                data-testid="btn-load-more-schools"
              >
                {loadingMore ? "Carregando…" : "Carregar mais escolas"}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
